import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { instance, pathname } from "./ActionCreators";
import notifyError from "../../utils/Notifications/Error";

export interface Robot {
    robot_id: string;
    name: string;
    status: "ok" | "down" | "partial" | string;
}
interface RobotStatus {
    robot_id: string;
    status: "ok" | "robot_stopped";
    event_type: string;
    message?: string;
    created_at?: string;
    mode: "full" | "partial" | "ok";
}
interface RobotsState {
    robots: Robot[];
    selectedRobotId: string | null;
    loading: boolean;
    error: string | null;
    robotStatuses: Record<string, RobotStatus>;
}

const initialState: RobotsState = {
    robots: [],
    selectedRobotId: null,
    loading: false,
    error: null,
    robotStatuses: {} as Record<string, RobotStatus>,
};

export const fetchRobots = createAsyncThunk<Robot[]>(
    "robots/fetchRobots",
    async (_, { rejectWithValue }) => {
        try {
            const response = await instance.get(`${pathname}/api/robots`);
            return response.data;
        } catch (err: any) {
            const msg =
                err.response?.data?.detail ||
                err.message ||
                "Не удалось загрузить список роботов";
            notifyError(msg);
            return rejectWithValue(msg);
        }
    },
);

const robotsSlice = createSlice({
    name: "robots",
    initialState,
    reducers: {
        setSelectedRobot: (state, action: PayloadAction<string | null>) => {
            state.selectedRobotId = action.payload;
            if (action.payload) {
                localStorage.setItem("selectedRobotId", action.payload);
            } else {
                localStorage.removeItem("selectedRobotId");
            }
        },
        clearRobotsError: (state) => {
            state.error = null;
        },
        updateRobotStatus: (
            state,
            action: PayloadAction<{
                robot_id: string;
                event_type: string;
                message?: string;
                created_at?: string;
                mode?: "full" | "partial" | "ok";
            }>,
        ) => {
            const { robot_id, event_type, message, created_at, mode } =
                action.payload;

            state.robotStatuses[robot_id] = {
                robot_id,
                status: mode === "ok" ? "ok" : "robot_stopped",
                event_type,
                message,
                created_at,
                mode: mode ?? "ok",
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchRobots.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchRobots.fulfilled, (state, action) => {
                state.loading = false;
                state.robots = action.payload;

                if (!state.selectedRobotId && action.payload.length > 0) {
                    const firstId = action.payload[0].robot_id;
                    state.selectedRobotId = firstId;
                    localStorage.setItem("selectedRobotId", firstId);
                }

                if (
                    state.selectedRobotId &&
                    !action.payload.some(
                        (r) => r.robot_id === state.selectedRobotId,
                    )
                ) {
                    if (action.payload.length > 0) {
                        const firstId = action.payload[0].robot_id;
                        state.selectedRobotId = firstId;
                        localStorage.setItem("selectedRobotId", firstId);
                    } else {
                        state.selectedRobotId = null;
                        localStorage.removeItem("selectedRobotId");
                    }
                }
            })
            .addCase(fetchRobots.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { setSelectedRobot, clearRobotsError, updateRobotStatus } =
    robotsSlice.actions;

export default robotsSlice.reducer;
